/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import gnoment from 'ui-cart/shared-components/utils/gnoment';
import * as types from '../constants';

export const dateFormat = 'YYYY-MM-DD';
export const dayFormat = 'ddd, MMM D, YYYY';
export const campingDateFormat = 'MMMM DD, YYYY';

export function castToGMT(aDate) {
    return new Date(gnoment.utc(aDate).format(dateFormat));
}

export function castToLocal(aDate) {
    return new Date(gnoment.utc(aDate).format(dayFormat));
}

export function castToUtcTimestamp(aDate) {
    return new gnoment.utc(aDate).hour(0).format();
}

export const getDatesFromRange = (startDate, endDate, inclusive) => {
    const dates = [];
    let start = gnoment(startDate).clone();
    const end = gnoment(endDate);

    if (!inclusive) {
        while (start.isBefore(end)) {
            const day = gnoment(start);
            dates.push(day);
            start = gnoment(start).add(1, 'days');
        }
        return dates;
    }
    while (start.isSameOrBefore(end)) {
        const day = gnoment(start);
        dates.push(day);
        start = gnoment(start).add(1, 'days');
    }
    return dates;
};

// getDatesFromReservation will return the start and end date of the reservation based upon inventory specific logic
export const getDatesFromReservation = (res) => {
    const dates = {
        start: false,
        end: false,
        endDisplay: false,

        // These are used by some inventories
        startTime: false,
        endTime: false,
    };

    switch (res.inventory_type) {
        case types.INVENTORY_CAMPING:
            dates.start = res.order_details.check_in;
            dates.end = res.order_details.check_out;
            break;
        case types.INVENTORY_PERMIT:
            dates.start = res.order_details.entry_date;
            if (res.order_details.exit_date) {
                dates.end = res.order_details.exit_date;
            }
            break;
        case types.INVENTORY_VEHICLE_PERMIT:
            dates.start = res.order_details.entry_date;
            dates.end = res.order_details.exit_date;
            break;
        case types.INVENTORY_TICKET:
            dates.start = res.order_details.tour_date;
            dates.startTime = res.order_details.tour_start;
            dates.endTime = res.order_details.tour_end;
            break;
        case types.INVENTORY_TIMED_ENTRY:
            dates.start = res.order_details.tour_date;
            dates.startTime = res.order_details.tour_start;
            dates.endTime = res.order_details.tour_end;
            break;
        case types.INVENTORY_PASS:
            dates.start = res.order_details.valid_on;
            if (res.order_details.expires) {
                dates.end = res.order_details.expires_on;
            }
            break;
        case types.INVENTORY_INTERAGENCYPASS:
            dates.start = res.order_details.valid_on;
            if (res.order_details.expires === 'false') {
                dates.endDisplay = types.LIFETIME_PASS_VALID_THROUGH;
            } else if (res.order_details.expires) {
                dates.end = res.order_details.expires_on;
            }
            break;
        case types.INVENTORY_PARKPASS:
            dates.start = res.order_details.valid_from;
            dates.end = res.order_details.valid_to;
            break;
        case types.INVENTORY_ACTIVITYPASS:
            dates.start = res.order_details.valid_from;
            dates.end = res.order_details.valid_to;
            break;
        case types.INVENTORY_TREE_PERMIT:
            dates.start = res.order_details.valid_from;
            dates.end = res.order_details.valid_to;
            break;
        case types.INVENTORY_RENTAL:
            dates.start = res.order_details.check_in;
            dates.end = res.order_details.check_out;
            break;
        case types.INVENTORY_LOTTERY_TICKET:
        case types.INVENTORY_LOTTERY_PERMIT:
        case types.INVENTORY_LOTTERY_CAMPING:
        case types.INVENTORY_QUEUE_LOTTERY:
            break;
        default:
            break;
    }

    return dates;
};

export const makeFormattedDatesString = (res, ticketAdjustMultiDayTicket) => {
    // Construct the date string
    let dateString = '';
    const dFormat = 'ddd ll';
    const dates = getDatesFromReservation(res);

    if (dates.start) {
        // Get TE valid days
        const validDays = res?.order_details?.valid_days || 0;
        // Validate if TE allow multiple days
        const allowMultiDays = ticketAdjustMultiDayTicket && validDays > 1;
        dateString = `${gnoment.utc(dates.start).format(dFormat)}`;

        if (!!allowMultiDays && res?.inventory_type === types?.INVENTORY_TIMED_ENTRY) {
            dateString = `${dateString} - ${gnoment
                ?.utc(dates?.start)
                ?.add(validDays - 1, 'day')
                ?.format(dFormat)}`;
        }
    }

    if (dates.end) {
        dateString = `${dateString} - ${gnoment.utc(dates.end).format(dFormat)}`;
    }

    if (dates.endDisplay) {
        dateString = `${dateString} - ${dates.endDisplay}`;
    }

    if (dates.startTime && dates.endTime) {
        dateString = `${dateString} (${dates.startTime} - ${dates.endTime})`;
    }

    return dateString;
};

export const cloneReservationStartDateToEasternMidnight = (reservationStart) => {
    return reservationStart.clone().tz('America/New_York').endOf('day');
};

export const checkInFlightFacilityTimeReservationStartDate = ({
    facilityTimeZone,
    reservationStart,
}) => {
    let limitDate;

    if (facilityTimeZone) {
        limitDate = reservationStart.clone().tz(facilityTimeZone).endOf('day');
    } else {
        limitDate = cloneReservationStartDateToEasternMidnight(reservationStart);
    }

    return gnoment().isAfter(limitDate);
};

export function getGnomentToday() {
    return gnoment().utc(true).startOf('day');
}

export function gnomentDateToUtcString(gnomentDate, format) {
    return gnomentDate.clone().startOf('day').utc(true).format(format);
}
